import React from 'react';
import PropTypes from 'prop-types';
import { Formik, getIn } from 'formik';
import * as Yup from 'yup';

import { useStyles } from '~/assets/styles';
import CheckboxFormik from '~/components/CheckboxFormik';
import Grid from '~/components/core/Atomic/Grid/Grid';
import DialogFooterActions from '~/components/core/DialogFooterActions';
import { useIncidentConfiguration } from '~/components/hooks/useIncidentConfiguration';
import { useLobConfiguration } from '~/components/hooks/useLobConfiguration';
import TextFieldFormik, {
  DatePickerTextFieldFormik,
  MonetaryValueTextFieldFormik,
  NumericTextFieldFormik,
  TimePickerTextFieldFormik,
} from '~/components/TextFieldFormik';
import {
  getPolicyNumberPrefix,
  isCancelledAfterEffectiveDT,
  isExpirationAfterEffectiveDT,
  isRetroactiveBeforeEffectiveDT,
} from '~/PolicyUtils';
import { COUNTRY_TO_STATE_MAP } from '~/Types';
import { isCountryWithStates } from '~/Utils';
import { getCurrencyFromCountry } from '~/Utils/currencyUtils';
import { getLocaleRegion } from '~/Utils/regionUtils';

import CardDialog from '../../../../CardDialog';
import ContactTextFieldFormik from '../../../../ContactTextFieldFormik';
import { Heading } from '../../../../core';
import CountryAutocompleteFormik from '../../../../CountryAutocompleteFormik';
import DisableableTooltip from '../../../../DisableableTooltip';
import useFetchExposures from '../../../../exposures/useFetchExposures';
import ConfiguredFields from '../../../../IncidentConfiguration/ConfiguredFields';
import { getAdditionalDataValidations } from '../../../../IncidentConfiguration/CustomFieldsContext';
import useOrganization from '../../../../OrganizationContext';
import StateAutocompleteFormik from '../../../../StateAutocompleteFormik';
import PolicyStateCountrySync, { normalizePolicyState } from '../../../PolicyStateCountrySync';
import { getBrokerFieldConfig } from '../brokerFieldConfig';

import styles from './EditPolicyDialog.module.scss';

function EditPolicyDialog(props) {
  const { onClose, onSaveClick, policy = {}, isSaving, policyStatus, lob, isEdit = true, claim } = props;
  const { classes } = useStyles();
  const { lobConfigurationsDict = {} } = useLobConfiguration();
  const { coverageConfiguration } = useOrganization();
  const { incidentConfiguration } = useIncidentConfiguration();
  const configuredFieldsConfiguration = incidentConfiguration?.policy?.configured_fields ?? [];
  const brokerConfig = getBrokerFieldConfig(incidentConfiguration);

  const { isExposuresLoading, isExposuresError, exposures } = useFetchExposures({ shouldFetch: !!claim && isEdit });

  // Check if payments have been made in the claim
  const hasPayments = React.useMemo(() => {
    if (isExposuresLoading || isExposuresError) return false;

    if (!claim || !isEdit) return false;

    // Check if any exposures have payments using the hook data
    const hasExposurePayments = exposures?.some((exposure) => {
      const indemnityPaid = exposure.indemnity.paid_sum || 0;
      const expensesPaid = exposure.expenses.paid_sum || 0;
      return indemnityPaid > 0 || expensesPaid > 0;
    });

    // Check if general expenses have payments
    const hasGeneralExpensePayments = (claim.general_expenses?.paid_sum || 0) > 0;

    return hasExposurePayments || hasGeneralExpensePayments;
  }, [claim, isEdit, exposures, isExposuresLoading, isExposuresError]);

  // Check if country selection should be disabled
  const isCountrySelectionDisabled = React.useMemo(() => {
    // If no payments were made, allow country selection
    if (!hasPayments) return false;

    // If payments were made, only allow country selection if policy_country is None
    if (hasPayments && !policy.policy_country) return false;

    // Disable if there are payments AND we're editing AND country is already set
    return hasPayments && isEdit && policy.policy_country;
  }, [hasPayments, isEdit, policy.policy_country]);

  const existingPolicyToFormikFields = (policy) => {
    const configuredFieldsInitialValues = {};
    configuredFieldsConfiguration.forEach((fieldConfig) => {
      configuredFieldsInitialValues[fieldConfig.id] = (policy.configured_fields_values ?? {})[fieldConfig.id];
    });
    const customFieldsInitialValues = {};
    if (policy.custom_fields?.length > 0) {
      policy.custom_fields.forEach((field) => {
        const key = Object.keys(field)[0];
        customFieldsInitialValues[key] = field[key].value;
      });
    }
    return {
      ...{
        policy_number: '',
        policy_effective_date: '',
        policy_expiration_date: '',
        insured_contact_id: '',
        broker_contact_id: null,
        is_claims_made: false,
        retroactive_date: '',
      },
      configured_fields_values: configuredFieldsInitialValues,
      ...policy,
      custom_fields: customFieldsInitialValues,
    };
  };

  const initialValues = existingPolicyToFormikFields(policy);

  const customFieldsValidationShape = {};
  if (policy.custom_fields?.length > 0) {
    policy.custom_fields.forEach((field) => {
      const key = Object.keys(field)[0];
      customFieldsValidationShape[key] = Yup.string().nullable();
    });
  }

  const validationSchema = Yup.object().shape({
    policy_number: Yup.string().required('Required'),
    policy_effective_date: Yup.string().required('Required'),
    effective_time: Yup.string()
      .matches(/^([0-1][0-9]|2[0-3]):[0-5][0-9](|:[0-5][0-9])$/, 'Invalid time supplied')
      .nullable(),
    policy_expiration_date: Yup.string()
      .required('Required')
      .test(
        'expiration_after_effective_dt',
        'Expiration date must be after effective date',
        isExpirationAfterEffectiveDT
      ),
    expiration_time: Yup.string()
      .matches(/^([0-1][0-9]|2[0-3]):[0-5][0-9](|:[0-5][0-9])$/, 'Invalid time supplied')
      .nullable(),
    cancelled_date: Yup.string()
      .test('cancelled_after_effective_dt', 'Cancelled date must be after effective date', isCancelledAfterEffectiveDT)
      .nullable(),
    cancelled_time: Yup.string()
      .matches(/^([0-1][0-9]|2[0-3]):[0-5][0-9](|:[0-5][0-9])$/, 'Invalid time supplied')
      .nullable(),
    insured_contact_id: Yup.number().required('Required'),
    broker_contact_id:
      brokerConfig?.active && brokerConfig?.mandatory ? Yup.number().required('Required') : Yup.number().nullable(),
    policy_country: Yup.string()
      .nullable()
      .test(
        'currency_matches_country',
        "Policy currency must match the selected country's default currency",
        function (value) {
          if (!value) return true; // Allow empty/null values

          // If no payments were made, allow any country selection
          if (!hasPayments) return true;

          const selectedCountryCurrency = getCurrencyFromCountry(value);
          const policyCurrency = this.parent.policy_currency;

          if (!selectedCountryCurrency || !policyCurrency) return true; // Skip validation if either is missing

          return selectedCountryCurrency === policyCurrency;
        }
      ),
    policy_state: Yup.string()
      .nullable()
      .test('policy-state-required-for-us', 'Required', function (value) {
        // State is required only for a US policy — matching the import rules and
        // HomeManualPolicyDetailsDialog, where US policy_state is mandatory while
        // other state-bearing countries (e.g. AU) leave it optional. Legacy
        // policies persisted before policy_country was captured fall back to the
        // org's locale region, mirroring the read-only PolicyDetailsCard. `||` (not
        // `??`) so a cleared autocomplete — which stores '' rather than null — also
        // falls back rather than being treated as a stateless country that strands a
        // stale policy_state.
        const effectiveCountry = this.parent?.policy_country || getLocaleRegion();
        // Evaluate the required rule against the country-normalised value, so a state
        // that is stale for the effective country (e.g. a US code left over after
        // switching to a state-bearing non-US country) can neither satisfy nor bypass
        // the rule even if validation runs before the sync effect clears the field.
        const normalizedState = normalizePolicyState(effectiveCountry, value);
        return effectiveCountry === 'US' ? Boolean(normalizedState) : true;
      }),
    configured_fields_values: Yup.object()
      .shape(getAdditionalDataValidations(configuredFieldsConfiguration))
      .nullable(),
    custom_fields: Yup.object().shape(customFieldsValidationShape).nullable(),
    retroactive_date: Yup.string()
      .nullable()
      .when('is_claims_made', {
        is: true,
        then: Yup.string()
          .nullable()
          .required('Required')
          .test(
            'retroactive_before_effective_dt',
            'Retroactive date must be before effective date',
            isRetroactiveBeforeEffectiveDT
          ),
        else: undefined,
      }),
    retroactive_time: Yup.string()
      .nullable()
      .when('is_claims_made', {
        is: true,
        then: Yup.string()
          .matches(/^([0-1][0-9]|2[0-3]):[0-5][0-9](|:[0-5][0-9])$/, 'Invalid time supplied')
          .nullable(),
        else: undefined,
      }),
    runoff_provision: Yup.number().nullable().when('is_claims_made', {
      is: true,
      then: Yup.number().nullable(),
      else: undefined,
    }),
    is_claims_made: Yup.boolean().nullable(),
  });

  const transformCustomFields = (values) => {
    const customFields = policy?.custom_fields ?? [];
    return [
      ...customFields.map((field) => {
        const key = Object.keys(field);
        field[key] = {
          ...field[key],
          value: values[key],
        };
        return {
          ...field,
        };
      }),
    ];
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={async (values, formikProps) => {
        try {
          // Normalise policy_state against the effective country at submit time too: the sync
          // effect clears stale values asynchronously, so guarantee the payload never carries a
          // state that is invalid for the country (e.g. a stale code on a non-US policy).
          // `||` (not `??`) so a cleared autocomplete ('' rather than null) also falls back.
          const effectiveCountry = getIn(values, 'policy_country') || getLocaleRegion();
          await onSaveClick({
            ...values,
            policy_state: normalizePolicyState(effectiveCountry, values.policy_state),
            custom_fields: transformCustomFields(values.custom_fields ?? {}),
          });
        } catch {
          formikProps.setSubmitting(false);
        }
      }}
    >
      {({ handleSubmit, values }) => {
        // Legacy policies persisted before policy_country was captured fall back to
        // the org's locale region, mirroring the read-only PolicyDetailsCard so the
        // Policy State field stays visible/required consistently across view and edit.
        // `||` (not `??`) so a cleared autocomplete ('' rather than null) also falls
        // back, keeping the field's visibility, the sync effect, validation, and submit
        // all keyed off the same effective country.
        const stateCountry = getIn(values, 'policy_country') || getLocaleRegion();
        return (
          <CardDialog
            isDialog
            title={`${isEdit ? 'Edit' : 'Set'} Policy Details`}
            maxWidth="sm"
            onClose={onClose}
            footerActions={
              <DialogFooterActions
                primaryLabel="Update"
                onClickPrimary={handleSubmit}
                onClickSecondary={onClose}
                disabled={isSaving}
              />
            }
          >
            <PolicyStateCountrySync country={stateCountry} />
            <Grid container spacing={5}>
              <Grid item xs={12}>
                <TextFieldFormik
                  id="policy_number"
                  label={getPolicyNumberPrefix(lob, lobConfigurationsDict) + ' Policy Number'}
                  fullWidth
                  className={classes.textField}
                />
              </Grid>
              <Grid item xs={6}>
                <CheckboxFormik id="is_claims_made" label="Claim Made Policy" fullWidth className={classes.textField} />
              </Grid>
              <Grid item xs={6}>
                <TextFieldFormik
                  id="policy_status"
                  label="Status"
                  fullWidth
                  disabled={true}
                  value={policyStatus}
                  className={classes.textField}
                />
              </Grid>
              <Grid item xs={6}>
                <ContactTextFieldFormik
                  id="insured_contact"
                  label="Policyholder"
                  fullWidth
                  acceptedRoles={['insured']}
                  className={classes.textField}
                  fixedSearchResults
                />
              </Grid>
              {brokerConfig?.active ? (
                <Grid item xs={6}>
                  <ContactTextFieldFormik
                    id="broker_contact"
                    label={brokerConfig.desc}
                    fullWidth
                    acceptedRoles={brokerConfig.acceptedRoles}
                    className={classes.textField}
                    fixedSearchResults
                    nullIfEmpty
                  />
                </Grid>
              ) : null}
              <Grid item xs={6}>
                <DatePickerTextFieldFormik
                  id="policy_effective_date"
                  label="Effective Date"
                  fullWidth
                  className={classes.textField}
                />
              </Grid>
              <Grid item xs={6}>
                <TimePickerTextFieldFormik
                  id="effective_time"
                  label="Effective Time"
                  fullWidth
                  className={classes.textField}
                />
              </Grid>
              <Grid item xs={6}>
                <DatePickerTextFieldFormik
                  id="cancelled_date"
                  label="Cancellation Date"
                  fullWidth
                  className={classes.textField}
                />
              </Grid>
              <Grid item xs={6}>
                <TimePickerTextFieldFormik
                  id="cancelled_time"
                  label="Cancellation Time"
                  fullWidth
                  className={classes.textField}
                />
              </Grid>
              <Grid item xs={6}>
                <DatePickerTextFieldFormik
                  id="policy_expiration_date"
                  label="Expiration Date"
                  fullWidth
                  className={classes.textField}
                />
              </Grid>
              <Grid item xs={6}>
                <TimePickerTextFieldFormik
                  id="expiration_time"
                  label="Expiration Time"
                  fullWidth
                  className={classes.textField}
                />
              </Grid>
              {values?.is_claims_made ? (
                <>
                  <Grid item xs={4}>
                    <DatePickerTextFieldFormik
                      id="retroactive_date"
                      label="Retroactive Date"
                      fullWidth
                      className={classes.textField}
                      disableFuture
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TimePickerTextFieldFormik
                      id="retroactive_time"
                      label="Retroactive Time"
                      fullWidth
                      className={classes.textField}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <NumericTextFieldFormik
                      id="runoff_provision"
                      label={<span className="text-sm">Days of Runoff Provision</span>}
                      fullWidth
                      className={classes.textField}
                    />
                  </Grid>
                </>
              ) : null}
              <Grid item xs={isCountryWithStates(stateCountry) ? 4 : 6}>
                <TextFieldFormik id="policy_type" label="Policy Type" fullWidth className={classes.textField} />
              </Grid>
              {isCountryWithStates(stateCountry) && (
                <Grid item xs={4}>
                  <StateAutocompleteFormik
                    id="policy_state"
                    label="Policy State"
                    statesDict={COUNTRY_TO_STATE_MAP[stateCountry || 'US']}
                    disabled={!COUNTRY_TO_STATE_MAP[stateCountry]}
                  />
                </Grid>
              )}
              <Grid item xs={isCountryWithStates(stateCountry) ? 4 : 6}>
                <DisableableTooltip
                  disabled={isCountrySelectionDisabled}
                  title={
                    isCountrySelectionDisabled
                      ? 'Policy country cannot be changed as payments have been made in this claim.'
                      : undefined
                  }
                >
                  <CountryAutocompleteFormik
                    id="policy_country"
                    label="Policy Country"
                    countriesConfigurationKey="contact_location"
                    disabled={isCountrySelectionDisabled}
                  />
                </DisableableTooltip>
              </Grid>
              <Grid item xs={6}>
                <MonetaryValueTextFieldFormik
                  id="limit_per_policy"
                  label="Policy Limit"
                  disabled={!coverageConfiguration.limit_per_policy}
                  fullWidth
                  className={classes.textField}
                />
              </Grid>
              <Grid item xs={6}>
                <MonetaryValueTextFieldFormik
                  id="limit_per_claim"
                  label="Claim Limit"
                  disabled={!coverageConfiguration.limit_per_claim}
                  fullWidth
                  className={classes.textField}
                />
              </Grid>
              <Grid item xs={6}>
                <DatePickerTextFieldFormik
                  id="renewal_date"
                  label="Renewal Date"
                  fullWidth
                  className={classes.textField}
                />
              </Grid>
              <Grid item xs={12}>
                <TextFieldFormik
                  id="policy_document_url"
                  label="Document URL"
                  fullWidth
                  className={classes.textField}
                />
              </Grid>
            </Grid>
            <div className="p-14 mt-20">
              {configuredFieldsConfiguration.length > 0 ? (
                <ConfiguredFields
                  values={values}
                  customFields={configuredFieldsConfiguration}
                  inline
                  wrapperProps={{ container: true, spacing: 5 }}
                />
              ) : null}
            </div>
            {policy.custom_fields?.length > 0 ? (
              <div className={styles.additionalInfoWrapper}>
                <Heading variant="h3">Additional Details</Heading>
                {policy.custom_fields?.length > 0 && (
                  <Grid container spacing={1}>
                    {policy.custom_fields.map((field) => (
                      <Grid item xs={6} key={Object.keys(field)[0]}>
                        <TextFieldFormik
                          id={'custom_fields.' + Object.keys(field)[0]}
                          label={field[Object.keys(field)[0]].display_name}
                          fullWidth
                          className={classes.textField}
                        />
                      </Grid>
                    ))}
                  </Grid>
                )}
              </div>
            ) : null}
          </CardDialog>
        );
      }}
    </Formik>
  );
}

EditPolicyDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSaveClick: PropTypes.func.isRequired,
  policy: PropTypes.object,
  isSaving: PropTypes.bool.isRequired,
  policyStatus: PropTypes.string.isRequired,
  lob: PropTypes.string.isRequired,
  isEdit: PropTypes.bool,
  claim: PropTypes.object,
};

export default EditPolicyDialog;
